import { Box, Grid, LinearProgress, Skeleton, Typography } from '@mui/material';
import { lighten } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { GetInvoiceMissionTypeDescription, InvoiceMissionTypeTextEnum } from 'src/shared/models/enums/invoiceMissionTypeText.enum';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import numberWithSpaces from 'src/shared/functions/numberWithSpaces';

type SendingSectionProps = {
  invoiceMission: InvoiceMissionResponse;
  loading?: boolean;
};

export const SendingSection = (props: SendingSectionProps) => {
  const { invoiceMission, loading } = props;
  const { t } = useTranslation();

  const displayDetails = (label: string, value: string | undefined, key: string | number, fullWidth = true) => {
    return (
      value && (
        <Grid item xs={fullWidth ? 'auto' : 3} mx={2} zeroMinWidth sx={{ maxWidth: fullWidth ? '100%' : '50%' }} key={key}>
          <Typography variant='body1' component='div' sx={{ fontWeight: 500 }}>
            {label.replace(/<BR>/gi, '')}
          </Typography>
          <Typography variant='body1' component='div'>
            {value}
          </Typography>
        </Grid>
      )
    );
  };

  const collectionRate = invoiceMission.totalAmount > 0 ? Math.round((invoiceMission.totalPaid / invoiceMission.totalAmount) * 1000) / 10 : 0;

  const paymentRate =
    invoiceMission.totalInvoiceCount > 0 ? Math.round((invoiceMission.paidInvoiceCount / invoiceMission.totalInvoiceCount) * 1000) / 10 : 0;

  const getProgressColor = (rate: number) => {
    if (rate >= 85) return lighten('#00af83', 0.4);
    if (rate >= 50) return lighten('#ff9800', 0.4);
    return lighten('#e11f51', 0.4);
  };

  if (loading) {
    return (
      <>
        <GridWrapper>
          <Grid item xs='auto' mx={2}>
            <Skeleton width={60} height={20} />
            <Skeleton width={100} height={24} />
          </Grid>
          <Grid item xs='auto' mx={2}>
            <Skeleton width={90} height={20} />
            <Skeleton width={60} height={24} />
          </Grid>
          <Grid item xs={3} mx={2}>
            <Skeleton width={50} height={20} />
            <Skeleton width={80} height={24} />
          </Grid>
        </GridWrapper>
        <Box sx={{ display: 'flex', gap: 2, mx: 2, mt: 2, mb: 1 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e0e0e0', flex: 1, maxWidth: 200 }}>
            <Skeleton width={100} height={16} />
            <Skeleton width={80} height={32} />
            <Skeleton height={6} sx={{ borderRadius: 3, mt: 1 }} />
            <Skeleton width={120} height={16} sx={{ mt: 0.5 }} />
          </Box>
          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e0e0e0', flex: 1, maxWidth: 200 }}>
            <Skeleton width={100} height={16} />
            <Skeleton width={80} height={32} />
            <Skeleton height={6} sx={{ borderRadius: 3, mt: 1 }} />
            <Skeleton width={120} height={16} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <GridWrapper>
        {displayDetails(
          t('invoice:invoiceMissionPage.type'),
          GetInvoiceMissionTypeDescription(invoiceMission.type as InvoiceMissionTypeTextEnum, t),
          'type',
        )}
        {displayDetails(
          t('invoice:invoiceMissionPage.numberOfInvoices'),
          t('invoice:invoiceMissionPage.invoiceCounter', { count: invoiceMission.data.totalCount }),
          'quantity',
        )}
        {displayDetails(t('invoice:invoiceMissionPage.created'), DateHelper.getDate(invoiceMission.date), 'date', false)}
      </GridWrapper>
      <Box sx={{ display: 'flex', gap: 2, mx: 2, mt: 2, mb: 1 }}>
        <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e0e0e0', flex: 1, maxWidth: 200 }}>
          <Typography variant='caption' color='text.secondary'>
            {t('invoice:invoiceMissionPage.collectionRate')}
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.2 }}>{collectionRate}%</Typography>
          <LinearProgress
            variant='determinate'
            value={Math.min(collectionRate, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              mt: 1,
              backgroundColor: '#e8e8e8',
              '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: getProgressColor(collectionRate) },
            }}
          />
          <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
            {numberWithSpaces(Math.round(invoiceMission.totalPaid))} / {numberWithSpaces(Math.round(invoiceMission.totalAmount))} kr
          </Typography>
        </Box>
        <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e0e0e0', flex: 1, maxWidth: 200 }}>
          <Typography variant='caption' color='text.secondary'>
            {t('invoice:invoiceMissionPage.paymentRate')}
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.2 }}>{paymentRate}%</Typography>
          <LinearProgress
            variant='determinate'
            value={Math.min(paymentRate, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              mt: 1,
              backgroundColor: '#e8e8e8',
              '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: getProgressColor(paymentRate) },
            }}
          />
          <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
            {numberWithSpaces(invoiceMission.paidInvoiceCount)} / {numberWithSpaces(invoiceMission.totalInvoiceCount)} st
          </Typography>
        </Box>
      </Box>
    </>
  );
};
